import React, { useState, useEffect } from 'react';
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import Typography from "@mui/material/Typography";
import { api } from "../../api/api";
import { ProjectAssignment, UiReceiverBase } from "../../api/models";
import ReceiverAutocomplete from "../../components/ReceiverAutocomplete";

interface Props {
  open: boolean;
  handleClose: () => void;
  assignment: ProjectAssignment | null;
  onSaved: (saved: ProjectAssignment) => void;
  onFetchFailed: (error: any) => void;
}

export default function ProjectAssignmentDetailView({ open, handleClose, assignment, onSaved, onFetchFailed }: Props) {
  const isNew = !assignment;
  const [personId, setPersonId] = useState<string>('');
  const [personName, setPersonName] = useState<string>('');
  const [projectName, setProjectName] = useState<string>('');
  const [startDate, setStartDate] = useState<string>('');
  const [managers, setManagers] = useState<UiReceiverBase[]>([]);
  const [hrs, setHrs] = useState<UiReceiverBase[]>([]);

  useEffect(() => {
    if (assignment) {
      setPersonId(assignment.person.id);
      setPersonName(assignment.person.name);
      setProjectName(assignment.projectName);
      setStartDate(assignment.startDate ? new Date(assignment.startDate).toISOString().split('T')[0] : '');
      setManagers(assignment.managers.map(m => ({ id: m.id, name: m.name, baseName: '', alternative: false })));
      setHrs(assignment.hrs.map(h => ({ id: h.id, name: h.name, baseName: '', alternative: false })));
    } else {
      setPersonId('');
      setPersonName('');
      setProjectName('');
      setStartDate('');
      setManagers([]);
      setHrs([]);
    }
  }, [assignment, open]);

  const handleSave = () => {
    const request = {
      personId,
      projectName,
      startDate,
      managerIds: managers.map(m => m.id),
      hrIds: hrs.map(h => h.id),
    };
    const action = assignment?.id
      ? api.projectAssignment.update(assignment.id, request)
      : api.projectAssignment.create(request);
    action
      .then((saved: any) => onSaved(saved))
      .catch(onFetchFailed);
  };

  return (
    <Dialog open={open} onClose={handleClose} scroll="paper" maxWidth="sm" fullWidth>
      <DialogTitle>{isNew ? 'New Project Assignment' : `Edit: ${assignment?.person.name} — ${assignment?.projectName}`}</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          {/* Person */}
          <Box>
            <Typography variant="caption" color="text.secondary">Person *</Typography>
            {personId ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Chip label={personName} onDelete={() => { setPersonId(''); setPersonName(''); }} />
              </Box>
            ) : (
              <ReceiverAutocomplete
                onFetchFailed={onFetchFailed}
                selectedReceivers={[]}
                handleReceiverAdd={(r: UiReceiverBase | null) => {
                  if (r) { setPersonId(r.id); setPersonName(r.name); }
                }}
              />
            )}
          </Box>

          {/* Project Name */}
          <TextField
            label="Project Name"
            value={projectName}
            onChange={e => setProjectName(e.target.value)}
            fullWidth
            required
          />

          {/* Start Date */}
          <TextField
            label="Start Date"
            type="date"
            value={startDate}
            onChange={e => setStartDate(e.target.value)}
            fullWidth
            required
            InputLabelProps={{ shrink: true }}
          />

          {/* Managers */}
          <Box>
            <Typography variant="caption" color="text.secondary">Managers</Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1 }}>
              {managers.map(m => (
                <Chip key={m.id} label={m.name} size="small"
                  onDelete={() => setManagers(prev => prev.filter(x => x.id !== m.id))} />
              ))}
            </Box>
            <ReceiverAutocomplete
              onFetchFailed={onFetchFailed}
              selectedReceivers={managers}
              handleReceiverAdd={(r: UiReceiverBase | null) => {
                if (r && !managers.find(m => m.id === r.id)) {
                  setManagers(prev => [...prev, r]);
                }
              }}
            />
          </Box>

          {/* HRs */}
          <Box>
            <Typography variant="caption" color="text.secondary">HRs</Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1 }}>
              {hrs.map(h => (
                <Chip key={h.id} label={h.name} size="small"
                  onDelete={() => setHrs(prev => prev.filter(x => x.id !== h.id))} />
              ))}
            </Box>
            <ReceiverAutocomplete
              onFetchFailed={onFetchFailed}
              selectedReceivers={hrs}
              handleReceiverAdd={(r: UiReceiverBase | null) => {
                if (r && !hrs.find(h => h.id === r.id)) {
                  setHrs(prev => [...prev, r]);
                }
              }}
            />
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
        <Button onClick={handleSave} variant="contained" disabled={!personId || !projectName || !startDate}>
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
}
